import { useCallback } from "react";
import { Box, Button, Flex, FlexProps, Text } from "@chakra-ui/react";
import { useReq } from "@larner.dev/use-req";
import { UserWithMediaAndGroups } from "@greenflagdate/shared";
import { Icon } from "../../../components/Icon/Icon";
import { apiReq } from "../../../lib/apiReq";
import { refreshUserState, store } from "../../../lib/store/store";
import { ActionType } from "../../../lib/store/storeActions";
import { toaster } from "../../../lib/toaster";

/**
 * What /spark shows a viewer who has Friends Only turned on. Sparking is a
 * dating surface and their profile is deliberately kept out of everyone's
 * deck, so instead of an empty deck we say why and offer the switch inline —
 * the same PUT /user/attributes the settings page makes, so the user doesn't
 * have to go hunting for the toggle to undo it.
 *
 * Flipping the flag updates the store, which is what re-runs the profile
 * fetch on the Spark page — there's no reload and no navigation.
 */
export const FriendsModeState = (props: FlexProps) => {
  const [updateUserReq, updateUserReqState] =
    useReq<UserWithMediaAndGroups>(apiReq);

  const switchToDating = useCallback(async () => {
    const response = await updateUserReq.put("/user/attributes", {
      friends_only: false,
    });
    if (response.success) {
      store.dispatch({
        type: ActionType.UpdateUser,
        params: { user: response.result },
      });
      // Past-spark participant categories are derived from this flag
      // server-side, so the cached pastSparks are stale the moment it flips.
      await refreshUserState();
    } else {
      toaster.create({
        type: "error",
        title: "Couldn't switch to dating mode.",
      });
    }
  }, [updateUserReq]);

  return (
    <Flex
      flex="1"
      direction="column"
      align="center"
      justify="center"
      textAlign="center"
      px="8"
      pt="6"
      pb="12"
      {...props}
    >
      <Flex
        align="center"
        justify="center"
        boxSize="5.25rem"
        borderRadius="full"
        bg="bg.friendsSoft"
        color="fg.friends"
        mb="7"
      >
        <Icon name="smile" boxSize="2.5rem" />
      </Flex>

      <Text
        as="h2"
        fontSize="xl"
        fontWeight="medium"
        color="fg.default"
        lineHeight="1.255"
        maxW="320px"
        mb="3.5"
      >
        You're in Friends Only mode
      </Text>
      <Text fontSize="sm" color="fg.muted" lineHeight="1.45" maxW="300px" mb="7">
        Sparking is for dating, so we've kept your profile out of it. You'll
        still match as friends after events you attend. Want to date too?
      </Text>

      <Button
        onClick={switchToDating}
        loading={updateUserReqState.loading}
        h="48px"
        px="7"
        borderRadius="full"
        bg="bg.brand"
        color="fg.onBrand"
        fontWeight="semibold"
        fontSize="sm"
        boxShadow="0 0 12.1px 2px rgba(0,0,0,0.05)"
      >
        Switch to Dating Mode
      </Button>
      <Box mt="3">
        <Text fontSize="xs" color="fg.muted">
          You can switch back any time in Dating Preferences.
        </Text>
      </Box>
    </Flex>
  );
};
